import { DatePipe } from "@angular/common";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from "@angular/core";
import { AbstractControl, FormArray, FormArrayName, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { FlexDirective, LayoutAlignDirective, LayoutDirective, FlexFillDirective } from "@ngbracket/ngx-layout/flex";
import { Command, CommandComponent, IconCommand } from "@tylertech/commands-module";
import { LabeledFieldComponent } from "@tylertech/common-components-module";
import { LgdFormControlFactory, LgdTextComponent, setEnabled } from "@tylertech/lgd-reactive-forms";
import { EventEmitterHelper } from "@tylertech/shared-module";
import { INote } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "note",
    templateUrl: "./note.tmpl.html",
    imports: [
        CommandComponent,
        DatePipe,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        FlexFillDirective,
        LabeledFieldComponent,
        LgdTextComponent,
        ReactiveFormsModule,
    ]
})
export class NoteComponent implements OnInit, OnDestroy
{
    private readonly formArrayDirective = inject(FormArrayName, { optional: true });

    @Input() readonly hideSubject: boolean;
    @Input() readonly model: INote;
    @Input() readonly viewOnly: boolean;

    // FIXME(DO-7969) - Rename, outputs shouldn't be named with the "on" prefix. that prefix should be reserved for event handler functions at are attached to events/outputs
    @Output() protected readonly onDelete = new EventEmitter<INote>();

    private readonly destructor$ = new Subject<void>();

    readonly noteForm: FormGroup;

    readonly deleteCommand: Command;

    constructor()
    {
        this.noteForm = new FormGroup({
            subject: LgdFormControlFactory.text(
                undefined,
                true,
                {
                    maxLength: 50,
                }),
            description: LgdFormControlFactory.text(
                undefined,
                true,
                {
                    maxLength: 1000,
                }),
        });

        this.deleteCommand = new IconCommand({
            icon: "delete",
            tooltip: "Delete Note",
            action: () => this.deleteNote(),
            getEnabled: () => !this.viewOnly,
        });
    }

    /** @deprecated - DO-7969 - there doesn't appear to be any usages where a formArrayName would be created. */
    private get parentFormArray(): FormArray | undefined { return this.formArrayDirective?.control; }

    protected get subjectControl(): AbstractControl<unknown> { return this.noteForm.get("subject"); }
    protected get descriptionControl(): AbstractControl<unknown> { return this.noteForm.get("description"); }

    private get canDelete(): boolean { return EventEmitterHelper.hasSubscribers(this.onDelete); }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        if (!this.model)
        {
            throw "note.component | Required binding missing: model";
        }

        // FIXME(DO-7969) - there doesn't appear to be any usages where a formArrayName would be created.
        if (this.parentFormArray)
        {
            this.parentFormArray.push(this.noteForm);
        }

        const self = this;

        setEnabled(this.subjectControl, !this.viewOnly)
            .setValue(this.model.Subject);
        subscribeToValueChanges(this.subjectControl, (newValue: string) =>
        {
            this.model.Subject = newValue;
        });

        setEnabled(this.descriptionControl, !this.viewOnly)
            .setValue(this.model.Description);
        subscribeToValueChanges(this.descriptionControl, (newValue: string) =>
        {
            this.model.Description = newValue;
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges
                .pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    protected deleteNote(): void
    {
        if (!this.canDelete)
        {
            return;
        }

        this.onDelete.emit(this.model);
    }
}
